import { LiveEventKind } from '@liks/backend-svc-api/esm/models/index';
import type { LiveEvent } from '../types/liveEvent';

const LIVE_EVENT_KINDS = new Set<string>(Object.values(LiveEventKind));

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object';
}

export function parseLiveEventPayload(payload: string): LiveEvent | null {
  const trimmed = payload.trim();
  if (trimmed === '') {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (!isRecord(parsed) || typeof parsed.kind !== 'string') {
      return null;
    }
    if (!LIVE_EVENT_KINDS.has(parsed.kind)) {
      return null;
    }
    return parsed as LiveEvent;
  } catch {
    return null;
  }
}

function extractCompleteJsonObject(
  text: string,
): { json: string; endIndex: number } | null {
  let depth = 0;
  let inString = false;
  let escaped = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];

    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (char === '\\') {
        escaped = true;
      } else if (char === '"') {
        inString = false;
      }
      continue;
    }

    if (char === '"') {
      inString = true;
      continue;
    }

    if (char === '{') {
      depth += 1;
    } else if (char === '}') {
      depth -= 1;
      if (depth === 0) {
        return { json: text.slice(0, index + 1), endIndex: index + 1 };
      }
    }
  }

  return null;
}

/** Feed incremental SSE/JSON text; returns unconsumed remainder. */
export function feedLiveEventStreamBuffer(
  buffer: string,
  onEvent: (event: LiveEvent) => void,
): string {
  let rest = buffer;

  while (rest.length > 0) {
    rest = rest.trimStart();
    if (rest.length === 0) {
      break;
    }

    if (
      rest.startsWith(':') ||
      rest.startsWith('event:') ||
      rest.startsWith('id:') ||
      rest.startsWith('retry:')
    ) {
      const lineEnd = rest.indexOf('\n');
      if (lineEnd === -1) {
        break;
      }
      rest = rest.slice(lineEnd + 1);
      continue;
    }

    if (rest.startsWith('data:')) {
      const afterPrefix = rest.slice(5);
      const lineEnd = afterPrefix.indexOf('\n');
      if (lineEnd === -1) {
        break;
      }
      const event = parseLiveEventPayload(afterPrefix.slice(0, lineEnd));
      if (event) {
        onEvent(event);
      }
      rest = afterPrefix.slice(lineEnd + 1);
      continue;
    }

    if (rest.startsWith('{')) {
      const extracted = extractCompleteJsonObject(rest);
      if (!extracted) {
        break;
      }

      const event = parseLiveEventPayload(extracted.json);
      if (event) {
        onEvent(event);
      }
      rest = rest.slice(extracted.endIndex);
      continue;
    }

    const lineEnd = rest.indexOf('\n');
    if (lineEnd === -1) {
      break;
    }

    rest = rest.slice(lineEnd + 1);
  }

  return rest;
}
